import React, { useEffect, useState, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { useDispatch, useSelector } from 'react-redux';
import ProductGrid from './ProductGrid';
import { fetchProductDetails, fetchSimilarProducts } from '../../../redux/slices/productsSlice';
import { addToCart } from '../../../redux/slices/cartSlice';
import { Truck, ChevronLeft, ChevronRight, Plus, Minus, ExternalLink, Lock, Unlock } from 'lucide-react';
import DOMPurify from 'dompurify';
import { useActiveOffer } from '../../offers/useOffer';
import { isEligible } from '../../offers/offerEngine';


// ─── Security ─────────────────────────────────────────────────────────────────
const sanitizeHTML = (dirty) => {
  if (!dirty) return '';
  return DOMPurify.sanitize(dirty, {
    ALLOWED_TAGS: ['b', 'i', 'em', 'strong', 'p', 'br', 'ul', 'ol', 'li'],
    ALLOWED_ATTR: [],
  });
};


 // ─── Constants ────────────────────────────────────────────────────────────────
const COLOR_MAP = {
  black: '#000000',
  white: '#ffffff',
  'OFF-White': '#FAF9F6',
  Jade: '#00A86B',
  red: '#ef4444',
  green: '#22c55e',
  yellow: '#facc15',
  gray: '#64748b',
  pink: '#f472b6',
  beige: '#f5f5dc',
  navy: '#000080',
  blue: '#2563eb',
  lightblue: '#add8e6',
  'light blue': '#add8e6',
  'Navy Blue': '#081820',
  'Petrol Blue': '#0c3238',
  'Royal Blue': '#141c45',
  'Olive Green': '#2e2e0d',
  'Golden Yellow': '#ffa100',
  'Mustard Yellow': '#cf9307',
  'Bottle Green': '#0a3818',
  'Choral': '#9e413e',
  'Grey Melange': '#cccac9',
  'Charcoal Grey': '#36454F',
  'Melange Grey': '#cccac9',
  'sunset saga': '#FD9C79',
  'urban forest': '#184A45',
  'midnight muse': '#232946',
  'cotton cloud': '#E9EAEC',
  'Light Baby Pink': '#FBD9D9',
  'Mint': '#B6FFBB',
  'Dark Wash': '#2C3E50',
  brown: '#8B4513',
  lavender: '#E6E6FA',
  'Baby Blue': '#89CFF0',
  mint: '#3EB489',
  'light baby pink': '#FFD1DF',

  // Two-color gradients
  'White Black': 'linear-gradient(45deg, #ffffff 50%, #000000 50%)',
  'Brown Black': 'linear-gradient(45deg, #8B4513 50%, #000000 50%)',
  'White Lavender': 'linear-gradient(45deg, #ffffff 50%, #E6E6FA 50%)',
  'Black - White': 'linear-gradient(45deg, #000000 50%, #ffffff 50%)',
  'White - Black': 'linear-gradient(45deg, #ffffff 50%, #000000 50%)',
  'Black - Charcoal Melange': 'linear-gradient(45deg, #000000 50%, #4A4A4A 50%)',
  'Maroon - OFF White': 'linear-gradient(45deg, #800000 50%, #F8F8F8 50%)',
  'Mustard Yellow - OFF White': 'linear-gradient(45deg, #D4A017 50%, #F8F8F8 50%)',
  'Coffee Brown - OFF White': 'linear-gradient(45deg, #6F4E37 50%, #F8F8F8 50%)',
  'Olive Green - OFF White': 'linear-gradient(45deg, #556B2F 50%, #F8F8F8 50%)',
};


const sizeCompliments = {
  XS: 'XS? Extra sleek, extra stunning!',
  S: "S stands for Supreme Style! You're going to own it.",
  M: "M means 'Masterpiece.' Perfect choice for a perfect fit.",
  L: "L is for Limitless Style — you're born to make a statement.",
  XL: "Extra Large, Extra Legendary — you're a total trendsetter!",
  '2XL': "Double the size, double the power — own it!",
  '3XL': "Crafted for comfort and charisma, just like you.",
  'Free Size': "One size fits all — designed for everyone!",
  'Free': "One size fits all — designed for everyone!",
};

const triggerHaptic = (type = 'medium') => {
  if ('vibrate' in navigator) {
    const patterns = { light: 10, medium: 20, heavy: 30, error: [20, 10, 20], success: [10, 5, 15] };
    navigator.vibrate(patterns[type] || patterns.medium);
  }
};

// ─── Component ────────────────────────────────────────────────────────────────
const ProductDetails = ({ productId }) => {
  const { id }     = useParams();
  const dispatch   = useDispatch();
  const { offer: activeOffer } = useActiveOffer();
  const { selectedProduct, loading, error, similarProducts } = useSelector((s) => s.products);
  const { user, guestId } = useSelector((s) => s.auth);

  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [selectedSize,      setSelectedSize]      = useState('');
  const [selectedColor,     setSelectedColor]     = useState('');
  const [quantity,          setQuantity]          = useState(1);
  const [isButtonDisabled,  setIsButtonDisabled]  = useState(false);
  const [addedToCart,       setAddedToCart]       = useState(false);
  const [showScrollTop,     setShowScrollTop]     = useState(false);
  const [isZoomed,          setIsZoomed]          = useState(false);
  const [mousePosition,     setMousePosition]     = useState({ x: 0, y: 0 });
  const [expandedSections,  setExpandedSections]  = useState({
    description: false, shipping: false, return: false, care: false,
  });
  const [touchStart,        setTouchStart]        = useState(0);
  const [touchEnd,          setTouchEnd]          = useState(0);
  const [isDragging,        setIsDragging]        = useState(false);
  const [dragOffset,        setDragOffset]        = useState(0);
  const [thumbTouchStart,   setThumbTouchStart]   = useState(0);
  const [thumbTouchEnd,     setThumbTouchEnd]     = useState(0);
  const [thumbScrollOffset, setThumbScrollOffset] = useState(0);

  // ─── Mystery Product ─────────────────────────────────────────────────────────
  const [unlockCode,   setUnlockCode]   = useState('');
  const [isUnlocked,   setIsUnlocked]   = useState(false);
  const [codeError,    setCodeError]    = useState(false);
  const [codeLoading,  setCodeLoading]  = useState(false);

  const imageContainerRef    = useRef(null);
  const thumbnailContainerRef = useRef(null);

  const productFetchId   = productId || id;
  const minSwipeDistance = 50;

  useEffect(() => {
    if (productFetchId) {
      dispatch(fetchProductDetails(productFetchId));
      dispatch(fetchSimilarProducts({ id: productFetchId }));
    }
  }, [dispatch, productFetchId]);

  const getFilteredImages = () => {
    if (!selectedProduct?.images || !selectedColor) return [];
    return selectedProduct.images.filter(img => img.color === selectedColor || !img.color);
  };

  const filteredImages = getFilteredImages();

  const getSizeChartIndex = () => {
    return filteredImages.findIndex(img => {
      if (img.color && selectedProduct.colors?.includes(img.color)) return false;
      const url = img.url.toLowerCase();
      return (
        url.includes('size_chart') || url.includes('size-chart') || url.includes('sizechart') ||
        url.includes('sizing') || url.includes('size_guide') ||
        (url.includes('size') && url.includes('chart'))
      );
    });
  };

  const isSizeChart = (index) => {
    const img = filteredImages[index];
    if (!img) return false;
    if (img.color && selectedProduct.colors?.includes(img.color)) return false;
    const url = img.url.toLowerCase();
    return (
      url.includes('size_chart') || url.includes('size-chart') || url.includes('sizechart') ||
      url.includes('sizing') || url.includes('size_guide') ||
      (url.includes('size') && url.includes('chart'))
    );
  };

  useEffect(() => {
    if (selectedProduct?.colors?.length > 0) setSelectedColor(selectedProduct.colors[0]);
    if (selectedProduct?.sizes?.length === 1) setSelectedSize(selectedProduct.sizes[0]);
    else setSelectedSize('');
    setCurrentImageIndex(0);
    setQuantity(1);
    setAddedToCart(false);
    setShowScrollTop(false);
  }, [selectedProduct]);

  useEffect(() => {
    setCurrentImageIndex(0);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [selectedColor]);

  useEffect(() => {
    const handleScroll = () => setShowScrollTop(addedToCart && window.scrollY > 300);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [addedToCart]);

  const handleSizeChartClick = () => {
    const chartIndex = getSizeChartIndex();
    if (chartIndex !== -1) {
      setCurrentImageIndex(chartIndex);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      triggerHaptic('light');
    }
  };

  const handleQuantityChange = (type) => {
    if (type === 'plus') setQuantity(p => p + 1);
    else if (type === 'minus' && quantity > 1) setQuantity(p => p - 1);
    triggerHaptic('light');
  };

  const handleAddToCart = () => {
    if (!selectedSize) { toast.error('Please select a size'); triggerHaptic('error'); return; }
    setIsButtonDisabled(true);
    dispatch(addToCart({
      productId: selectedProduct._id,
      quantity, userId: user?._id, guestId,
      size: selectedSize, color: selectedColor,
    }));
    setAddedToCart(true);
    triggerHaptic('success');
    toast.success('Added to cart!');
    setTimeout(() => setIsButtonDisabled(false), 1500);
  };

  // ─── Mystery: detect + unlock ─────────────────────────────────────────────
  const isMystery = selectedProduct?.name?.toLowerCase().includes('mystery');

  const handleCodeSubmit = async () => {
    if (unlockCode.length !== 4) return;
    setCodeLoading(true);

    try {
      // ── Server-side validation (recommended) ──
      // const res = await fetch('/api/mystery/validate', {
      //   method: 'POST',
      //   headers: { 'Content-Type': 'application/json' },
      //   body: JSON.stringify({ code: unlockCode, productId: selectedProduct._id }),
      // });
      // const data = await res.json();
      // if (data.valid) { setIsUnlocked(true); triggerHaptic('success'); toast.success('Access granted.'); }
      // else { setCodeError(true); triggerHaptic('error'); setTimeout(() => setCodeError(false), 1500); }

      // ── Client-side fallback (swap out when backend is ready) ──
      await new Promise(r => setTimeout(r, 600)); // simulate network
      if (unlockCode === '2002') {
        setIsUnlocked(true);
        setCodeError(false);
        triggerHaptic('success');
        toast.success('Access granted. Cart unlocked.');
      } else {
        setCodeError(true);
        triggerHaptic('error');
        setTimeout(() => setCodeError(false), 1500);
      }
    } finally {
      setCodeLoading(false);
    }
  };

  const scrollToTop = () => { window.scrollTo({ top: 0, behavior: 'smooth' }); triggerHaptic('light'); };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
    triggerHaptic('light');
  };

  const goToNextImage     = () => { setCurrentImageIndex(p => (p + 1) % filteredImages.length); triggerHaptic('light'); };
  const goToPreviousImage = () => { setCurrentImageIndex(p => (p - 1 + filteredImages.length) % filteredImages.length); triggerHaptic('light'); };

  const handleTouchStart = (e) => { setTouchStart(e.targetTouches[0].clientX); setTouchEnd(e.targetTouches[0].clientX); setIsDragging(true); };
  const handleTouchMove  = (e) => { if (!isDragging) return; setTouchEnd(e.targetTouches[0].clientX); setDragOffset(touchStart - e.targetTouches[0].clientX); };
  const handleTouchEnd   = () => {
    if (!isDragging) return;
    setIsDragging(false); setDragOffset(0);
    const distance = touchStart - touchEnd;
    if (Math.abs(distance) > minSwipeDistance) { distance > 0 ? goToNextImage() : goToPreviousImage(); }
  };

  const handleThumbTouchStart = (e) => setThumbTouchStart(e.targetTouches[0].clientX);
  const handleThumbTouchMove  = (e) => { setThumbTouchEnd(e.targetTouches[0].clientX); setThumbScrollOffset(thumbTouchStart - thumbTouchEnd); };
  const handleThumbTouchEnd   = () => { if (thumbnailContainerRef.current) thumbnailContainerRef.current.scrollLeft += thumbScrollOffset; setThumbScrollOffset(0); };

  const handleMouseMove = (e) => {
    if (!imageContainerRef.current) return;
    const { left, top, width, height } = imageContainerRef.current.getBoundingClientRect();
    setMousePosition({ x: ((e.clientX - left) / width) * 100, y: ((e.clientY - top) / height) * 100 });
  };

  const calculateDiscount = (original, current) => {
    if (!original || original <= current) return 0;
    return Math.round(((original - current) / original) * 100);
  };

  // ─── LOADING ────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div style={{ minHeight: '60vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '16px', fontFamily: "'Exo 2', sans-serif", background: '#f8f8f8' }}>
        <img
          src="https://res.cloudinary.com/dmrlotfsv/image/upload/w_512,f_auto,q_auto/t_VESHH-EON_WEBSITE/VESHH-EON_TYPE_WEB_ppfyxy"
          alt="Loading"
          style={{ width: '120px', height: 'auto' }}
        />
        <span style={{ fontSize: '9px', fontWeight: 400, letterSpacing: '0.4em', textTransform: 'uppercase', color: 'black' }}>
          Loading...
        </span>
      </div>
    );
  }

  // ─── ERROR ──────────────────────────────────────────────────────────────────
  if (error) {
    return (
      <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8f8f8', fontFamily: "'Exo 2', sans-serif" }}>
        <div style={{ textAlign: 'center' }}>
          <p style={{ fontSize: '11px', fontWeight: 500, letterSpacing: '0.35em', textTransform: 'uppercase', color: 'black', marginBottom: '8px' }}>
            Failed to load product
          </p>
          <p style={{ fontSize: '10px', fontWeight: 300, letterSpacing: '0.15em', color: 'black' }}>{error}</p>
        </div>
      </div>
    );
  }

  if (!selectedProduct) return null;

  const isSingleSize  = selectedProduct.sizes?.length === 1;
  const sizeChartIndex = getSizeChartIndex();
  const discount       = calculateDiscount(selectedProduct.originalPrice, selectedProduct.price);
  const isOutOfStock   = selectedProduct.countInStock === 0 || selectedProduct.inStock === false;
  const isLowStock     = !isOutOfStock && selectedProduct.stock > 0 && selectedProduct.stock < 10;

  return (
    <>
      <style>{pdStyles}</style>

      <div className="pd-root">
        <div className="pd-grid">

          {/* ── IMAGE GALLERY ── */}
          <div className="pd-gallery">

            {/* Main image */}
            <div
              className="pd-main-img-wrap"
              ref={imageContainerRef}
              onMouseMove={handleMouseMove}
              onMouseEnter={() => setIsZoomed(true)}
              onMouseLeave={() => setIsZoomed(false)}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            >
              {filteredImages.length > 0 ? (
                <>
                  <img
                    src={filteredImages[currentImageIndex]?.url}
                    alt={`${selectedProduct.name} - ${selectedColor}`}
                    className="pd-main-img"
                    style={{
                      objectFit: isSizeChart(currentImageIndex) ? 'contain' : 'cover',
                      padding: isSizeChart(currentImageIndex) ? '16px' : '0',
                      transform: isZoomed && !isSizeChart(currentImageIndex)
                        ? `scale(2) translate(${50 - mousePosition.x}%, ${50 - mousePosition.y}%)`
                        : isDragging ? `translateX(${-dragOffset}px)` : 'none',
                      transformOrigin: 'center',
                      // filter: isMystery && !isUnlocked ? 'blur(18px) brightness(0.55)' : 'none',
                      // transition: 'filter 0.5s ease',
                      // pointerEvents: isMystery && !isUnlocked ? 'none' : undefined,
                    }}
                  />

                  {/* Mystery overlay */}
                  {isMystery && !isUnlocked && (
                    <div className="pd-mystery-img-overlay">
                      <Lock size={28} strokeWidth={1.5} color="#fff" />
                      <span className="pd-mystery-img-label">MYSTERY DROP</span>
                    </div>
                  )}

                  {isSizeChart(currentImageIndex) && (
                    <span className="pd-sizechart-badge">Size Chart</span>
                  )}

                  {/* Nav arrows */}
                  <button className="pd-arrow pd-arrow--left" onClick={goToPreviousImage} aria-label="Previous image">
                    <ChevronLeft size={18} />
                  </button>
                  <button className="pd-arrow pd-arrow--right" onClick={goToNextImage} aria-label="Next image">
                    <ChevronRight size={18} />
                  </button>

                  {/* Dot indicators */}
                  <div className="pd-dots">
                    {filteredImages.map((_, idx) => (
                      <button
                        key={idx}
                        className={`pd-dot ${currentImageIndex === idx ? 'pd-dot--active' : ''}`}
                        onClick={() => { setCurrentImageIndex(idx); triggerHaptic('light'); }}
                        aria-label={`Image ${idx + 1}`}
                      />
                    ))}
                  </div>
                </>
              ) : (
                <div className="pd-no-img">No images available</div>
              )}
            </div>

            {/* Thumbnails */}
            <div
              ref={thumbnailContainerRef}
              className="pd-thumbs"
              onTouchStart={handleThumbTouchStart}
              onTouchMove={handleThumbTouchMove}
              onTouchEnd={handleThumbTouchEnd}
            >
              {filteredImages.map((img, idx) => (
                <button
                  key={idx}
                  className={`pd-thumb ${currentImageIndex === idx ? 'pd-thumb--active' : ''}`}
                  onClick={() => { setCurrentImageIndex(idx); triggerHaptic('light'); }}
                >
                  <img
                    src={img.url}
                    alt={`Thumbnail ${idx + 1}`}
                    style={{ objectFit: isSizeChart(idx) ? 'contain' : 'cover', padding: isSizeChart(idx) ? '4px' : '0' }}
                  />
                </button>
              ))}
            </div>
          </div>

          {/* ── PRODUCT INFO ── */}
          <div className="pd-info">

            {/* Name + Price */}
            <div className="pd-info-top">
              {selectedProduct.category && (
                <span className="pd-category">{selectedProduct.category}</span>
              )}
              <h1 className="pd-name">{selectedProduct.name}</h1>

              <div className="pd-price-row">
                <span className="pd-price">₹{selectedProduct.price}</span>
                {selectedProduct.originalPrice && selectedProduct.originalPrice > selectedProduct.price && (
                  <>
                    <span className="pd-original-price">₹{selectedProduct.originalPrice}</span>
                    <span className="pd-discount-badge">-{discount}%</span>
                  </>
                )}
              </div>

              {activeOffer && isEligible(activeOffer.eligible, selectedProduct._id) && (
                <Link to="/bogo" className="pd-bogo">
                  <span className="pd-bogo-tag">BOGO</span>
                  <span className="pd-bogo-text">Add any 2 eligible items — the cheaper one is free</span>
                </Link>
              )}

              {isLowStock && (
                <span className="pd-low-stock">Only {selectedProduct.stock} left</span>
              )}
            </div>

            <div className="pd-divider" />

            {/* Color */}
            {selectedProduct.colors?.length > 0 && (
              <div className="pd-section">
                <div className="pd-section-label">
                  Color — <span style={{ fontWeight: 400, textTransform: 'capitalize' }}>{selectedColor}</span>
                </div>
                <div className="pd-colors">
                  {selectedProduct.colors.map((color) => (
                    <button
                      key={color}
                      type="button"
                      className={`pd-color-btn ${selectedColor === color ? 'pd-color-btn--active' : ''}`}
                      style={{ background: COLOR_MAP[color] || color }}
                      onClick={() => { setSelectedColor(color); triggerHaptic('light'); }}
                      aria-label={`Select ${color}`}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Size */}
            {selectedProduct.sizes?.length > 0 && (
              <div className="pd-section">
                <div className="pd-section-label-row">
                  <span className="pd-section-label">
                    Size{selectedSize && ` — ${selectedSize}`}
                  </span>
                  {sizeChartIndex !== -1 && (
                    <button className="pd-size-chart-link" onClick={handleSizeChartClick}>
                      View Size Chart
                    </button>
                  )}
                </div>

                {!isSingleSize && !selectedSize && (
                  <p className="pd-size-hint">Please select a size</p>
                )}

                <div className="pd-sizes">
                  {selectedProduct.sizes.map((size) => (
                    <button
                      key={size}
                      type="button"
                      className={`pd-size-btn ${selectedSize === size ? 'pd-size-btn--active' : ''}`}
                      onClick={() => setSelectedSize(size)}
                      disabled={isSingleSize}
                    >
                      {size}
                    </button>
                  ))}
                </div>

                {selectedSize && sizeCompliments[selectedSize] && (
                  <p className="pd-size-compliment">{sizeCompliments[selectedSize]}</p>
                )}
              </div>
            )}

            {/* Quantity */}
            {!isMystery && <div className="pd-section">
              <div className="pd-section-label">Quantity</div>
              <div className="pd-qty">
                <button
                  type="button"
                  className="pd-qty-btn"
                  onClick={() => handleQuantityChange('minus')}
                  disabled={quantity <= 1}
                >
                  —
                </button>
                <span className="pd-qty-num">{quantity}</span>
                <button
                  type="button"
                  className="pd-qty-btn"
                  onClick={() => handleQuantityChange('plus')}
                >
                  +
                </button>
              </div>
            </div>}

            <div className="pd-divider" />

            {/* Shipping note */}
            <div className="pd-shipping-note">
              <Truck size={14} strokeWidth={1.75} />
              <span>Free Shipping on all orders</span>
            </div>

            {/* Add to Cart — Mystery or Normal */}
            {isMystery && !isUnlocked ? (
              <div className="pd-mystery-lock">
                <div className="pd-mystery-lock-header">
                  <Lock size={13} strokeWidth={2} />
                  <span>ACCESS LOCKED</span>
                </div>

                <div className="pd-mystery-input-row">
                  <input
                    className={`pd-mystery-input ${codeError ? 'pd-mystery-input--error' : ''}`}
                    type="text"
                    inputMode="numeric"
                    maxLength={4}
                    placeholder="_ _ _ _"
                    value={unlockCode}
                    onChange={(e) => setUnlockCode(e.target.value.replace(/\D/g, ''))}
                    onKeyDown={(e) => e.key === 'Enter' && handleCodeSubmit()}
                    autoComplete="off"
                  />
                  <button
                    className="pd-mystery-submit"
                    onClick={handleCodeSubmit}
                    disabled={unlockCode.length !== 4 || codeLoading}
                  >
                    {codeLoading ? '...' : 'UNLOCK'}
                  </button>
                </div>

                {codeError && (
                  <p className="pd-mystery-error">Invalid code. Check your email and try again.</p>
                )}

                {/* Newsletter hint */}
                <div className="pd-mystery-hint">
                  <div className="pd-mystery-hint-rule" />
                  <p className="pd-mystery-hint-text">
                    Subscribe to our newsletter — lucky ones receive the unlock code in their inbox.
                  </p>
                  <button
                    className="pd-mystery-newsletter-btn"
                    onClick={() => window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' })}
                  >
                    Subscribe &rarr;
                  </button>
                </div>
              </div>
            ) : (
              <>
                <button
                  className={`pd-atc-btn ${isOutOfStock ? 'pd-atc-btn--out' : ''}`}
                  onClick={handleAddToCart}
                  disabled={isButtonDisabled || isOutOfStock}
                >
                  {isOutOfStock ? 'Out of Stock' : (
                    isMystery ? (
                      <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                        <Unlock size={14} strokeWidth={2} /> Add to Cart
                      </span>
                    ) : 'Add to Cart'
                  )}
                </button>

                {isOutOfStock && (
                  <div className="pd-restock">
                    <p className="pd-restock-text">Want to be notified when this item is back?</p>
                    <button
                      className="pd-restock-link"
                      onClick={() => window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' })}
                    >
                      Subscribe to restock alerts
                    </button>
                  </div>
                )}

                {addedToCart && (
                  <p className="pd-added-note">Added to cart — open cart to checkout.</p>
                )}
              </>
            )}

            <div className="pd-divider" />

            {/* Accordion sections */}
            <div className="pd-accordions">
              {['description', 'shipping', 'return', 'care'].map((section) => (
                <div key={section} className="pd-accordion">
                  <button
                    className="pd-accordion-trigger"
                    onClick={() => toggleSection(section)}
                  >
                    <span>
                      {section === 'return' ? 'Return Policy'
                        : section === 'care' ? 'Care Instructions'
                        : section.charAt(0).toUpperCase() + section.slice(1)}
                    </span>
                    <span
                      className="pd-accordion-icon"
                      style={{ transform: expandedSections[section] ? 'rotate(180deg)' : 'rotate(0)' }}
                    >
                      <ChevronLeft size={12} style={{ transform: 'rotate(-90deg)' }} />
                    </span>
                  </button>

                  <div
                    className="pd-accordion-body"
                    style={{ maxHeight: expandedSections[section] ? '2000px' : '0' }}
                  >
                    <div className="pd-accordion-content">
                      {section === 'description' && (
                        <div style={{ textAlign: 'justify' }}>
                          {Array.isArray(selectedProduct.description)
                            ? selectedProduct.description.map((desc, idx) => (
                                <div key={idx} className="pd-desc-block" dangerouslySetInnerHTML={{ __html: sanitizeHTML(desc) }} />
                              ))
                            : <div dangerouslySetInnerHTML={{ __html: sanitizeHTML(selectedProduct.description) }} />
                          }
                        </div>
                      )}
                      {section === 'shipping' && (
                        <>
                          <p className="pd-acc-text">Every VESHH-EON piece is crafted with intention — designed just for you. Our shipping process reflects the same care and precision, ensuring your order reaches you safely, wherever you are.</p>
                          <p className="pd-acc-label">Free Shipping</p>
                          <p className="pd-acc-text" style={{ marginBottom: '12px' }}>Available for all orders</p>
                          <a href="https://www.veshheon.in/shipping" target="_blank" rel="noopener noreferrer" className="pd-acc-link">
                            Know more <ExternalLink size={11} />
                          </a>
                        </>
                      )}
                      {section === 'return' && (
                        <>
                          <p className="pd-acc-text">Your satisfaction is our priority. We believe in making returns and refunds as simple and transparent as possible.</p>
                          <p className="pd-acc-tip">Tip: Record the unboxing video in case of damage — it helps ease returns and speeds up refunds.</p>
                          <a href="https://www.veshheon.in/returnpolicy" target="_blank" rel="noopener noreferrer" className="pd-acc-link">
                            Know more <ExternalLink size={11} />
                          </a>
                        </>
                      )}
                      {section === 'care' && (
                        <ul className="pd-care-list">
                          {[
                            'Machine wash cold with similar colors',
                            'Use mild detergent, avoid bleach',
                            'Tumble dry low or hang dry',
                            'Iron on low heat if needed, inside out',
                            'Do not dry clean',
                            'Store folded in a cool, dry place',
                          ].map((item, i) => (
                            <li key={i} className="pd-care-item">
                              <span className="pd-care-dash">—</span>
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Brand / Material */}
            {(selectedProduct.brand || selectedProduct.material) && (
              <div className="pd-meta">
                {selectedProduct.brand && (
                  <div className="pd-meta-row">
                    <span className="pd-meta-label">Brand</span>
                    <span className="pd-meta-value">{selectedProduct.brand}</span>
                  </div>
                )}
                {selectedProduct.material && (
                  <div className="pd-meta-row">
                    <span className="pd-meta-label">Material</span>
                    <span className="pd-meta-value">{selectedProduct.material}</span>
                  </div>
                )}
              </div>
            )}

          </div>
        </div>

        {/* ── SIMILAR PRODUCTS ── */}
        {similarProducts && similarProducts.length > 0 && (
          <div className="pd-similar">
            <div className="pd-similar-header">
              <span className="pd-similar-eyebrow">Explore</span>
              <h2 className="pd-similar-title">
                You Might <span className="pd-similar-bold">Also Like</span>
              </h2>
            </div>
            <div className="pd-similar-rule"><hr /></div>
            <ProductGrid products={similarProducts} />
          </div>
        )}

        {/* ── SCROLL TO TOP ── */}
        {showScrollTop && (
          <button className="pd-scroll-top" onClick={scrollToTop} aria-label="Scroll to top">
            <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
            </svg>
          </button>
        )}

      </div>
    </>
  );
};

/* ── STYLES ── */
const pdStyles = `
  @import url('https://fonts.googleapis.com/css2?family=Exo+2:wght@200;300;400;500;600&display=swap');

  .pd-root {
    background: #f8f8f8;
    min-height: 100vh;
    padding: 48px 80px 96px;
    font-family: 'Exo 2', sans-serif;
    max-width: 1400px;
    margin: 0 auto;
  }

  /* ── GRID ── */
  .pd-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 64px;
    align-items: start;
  }

  /* ── GALLERY ── */
  .pd-gallery {
    display: flex;
    flex-direction: column;
    gap: 10px;
    position: sticky;
    top: 90px;
  }

  .pd-main-img-wrap {
    position: relative;
    width: 100%;
    aspect-ratio: 3 / 4;
    background: #efefef;
    overflow: hidden;
    cursor: zoom-in;
  }

  .pd-main-img {
    width: 100%;
    height: 100%;
    display: block;
    transition: transform 0.3s ease;
  }

  .pd-sizechart-badge {
    position: absolute;
    top: 12px;
    left: 12px;
    background: #fff;
    color: #000;
    font-family: 'Exo 2', sans-serif;
    font-size: 7px;
    font-weight: 600;
    letter-spacing: 0.3em;
    text-transform: uppercase;
    padding: 4px 10px;
    z-index: 3;
  }

  .pd-arrow {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    width: 36px;
    height: 36px;
    background: rgba(248,248,248,0.88);
    border: 1px solid rgba(0,0,0,0.1);
    color: #000;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    opacity: 0;
    transition: opacity 0.2s ease;
    z-index: 3;
  }
  .pd-main-img-wrap:hover .pd-arrow { opacity: 1; }
  .pd-arrow--left  { left: 10px; }
  .pd-arrow--right { right: 10px; }
  .pd-arrow:hover  { background: #fff; }

  .pd-dots {
    position: absolute;
    bottom: 12px;
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    gap: 5px;
    z-index: 3;
  }
  .pd-dot {
    width: 6px; height: 6px;
    background: black;
    border: none; cursor: pointer;
    transition: all 0.2s;
  }
  .pd-dot--active { width: 20px; background: #fff; }

  .pd-no-img {
    width: 100%; height: 100%;
    display: flex; align-items: center; justify-content: center;
    font-size: 10px; letter-spacing: 0.3em; text-transform: uppercase;
    color: black;
  }

  /* Thumbnails */
  .pd-thumbs {
    display: flex;
    gap: 6px;
    overflow-x: auto;
    scrollbar-width: none;
    -ms-overflow-style: none;
    padding-bottom: 2px;
  }
  .pd-thumbs::-webkit-scrollbar { display: none; }

  .pd-thumb {
    flex-shrink: 0;
    width: 72px; height: 72px;
    border: 1px solid black;
    overflow: hidden;
    background: #efefef;
    cursor: pointer;
    transition: border-color 0.2s ease, transform 0.2s ease;
  }
  .pd-thumb img { width: 100%; height: 100%; display: block; }
  .pd-thumb--active { border-color: #000; transform: scale(1.04); }
  .pd-thumb:hover:not(.pd-thumb--active) { border-color: black); }

  /* ── INFO ── */
  .pd-info {
    display: flex;
    flex-direction: column;
    gap: 0;
  }

  .pd-info-top { margin-bottom: 20px; }

  .pd-category {
    display: block;
    font-size: 8px;
    font-weight: 400;
    letter-spacing: 0.45em;
    text-transform: uppercase;
    color: black;
    margin-bottom: 10px;
  }

  .pd-name {
    font-size: clamp(20px, 2.5vw, 32px);
    font-weight: 300;
    color: #000;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    line-height: 1.1;
    margin: 0 0 16px;
  }

  .pd-bogo {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-top: 14px;
    padding: 10px 12px;
    border: 1px solid #000;
    text-decoration: none;
    color: #000;
  }
  .pd-bogo-tag {
    background: #000;
    color: #fff;
    font-size: 8px;
    font-weight: 600;
    letter-spacing: 0.3em;
    text-transform: uppercase;
    padding: 4px 8px;
  }
  .pd-bogo-text {
    font-size: 9px;
    font-weight: 400;
    letter-spacing: 0.18em;
    text-transform: uppercase;
  }

  .pd-price-row {
    display: flex;
    align-items: baseline;
    gap: 10px;
    flex-wrap: wrap;
  }

  .pd-price {
    font-size: 24px;
    font-weight: 500;
    letter-spacing: 0.05em;
    color: #000;
  }

  .pd-original-price {
    font-size: 14px;
    font-weight: 300;
    color: black;
    text-decoration: line-through;
    letter-spacing: 0.05em;
  }

  .pd-discount-badge {
    font-size: 8px;
    font-weight: 600;
    letter-spacing: 0.25em;
    text-transform: uppercase;
    background: #000;
    color: #fff;
    padding: 3px 8px;
  }

  .pd-low-stock {
    display: block;
    margin-top: 8px;
    font-size: 8px;
    font-weight: 500;
    letter-spacing: 0.3em;
    text-transform: uppercase;
    color: #b45309;
  }

  /* Divider */
  .pd-divider {
    height: 1px;
    background: rgba(0,0,0,0.08);
    margin: 20px 0;
  }

  /* Sections */
  .pd-section { margin-bottom: 20px; }

  .pd-section-label {
    font-size: 8px;
    font-weight: 500;
    letter-spacing: 0.45em;
    text-transform: uppercase;
    color: black;
    margin-bottom: 12px;
    display: block;
  }

  .pd-section-label-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;
  }

  .pd-size-chart-link {
    font-size: 8px;
    font-weight: 400;
    letter-spacing: 0.3em;
    text-transform: uppercase;
    color: black;
    background: transparent;
    border: none;
    cursor: pointer;
    text-decoration: underline;
    transition: color 0.2s;
  }
  .pd-size-chart-link:hover { color: #000; }

  .pd-size-hint {
    font-size: 8px;
    font-weight: 400;
    letter-spacing: 0.25em;
    text-transform: uppercase;
    color: black;
    margin-bottom: 10px;
  }

  /* Colors */
  .pd-colors { display: flex; flex-wrap: wrap; gap: 8px; }

  .pd-color-btn {
    width: 32px; height: 32px;
    border: 2px solid black;
    cursor: pointer;
    transition: border-color 0.2s, transform 0.2s;
    border-radius: 50%;
  }
  .pd-color-btn--active { border-color: #000; transform: scale(1.15); }
  .pd-color-btn:hover:not(.pd-color-btn--active) { border-color: black; }

  /* Sizes */
  .pd-sizes { display: flex; flex-wrap: wrap; gap: 6px; }

  .pd-size-btn {
    min-width: 44px;
    height: 36px;
    padding: 0 12px;
    border: 1px solid black;
    background: transparent;
    color: black;
    font-family: 'Exo 2', sans-serif;
    font-size: 10px;
    font-weight: 500;
    letter-spacing: 0.2em;
    text-transform: uppercase;
    cursor: pointer;
    transition: all 0.15s ease;
  }
  .pd-size-btn:hover:not(:disabled):not(.pd-size-btn--active) { border-color: #000; color: #000; }
  .pd-size-btn--active { background: #000; color: #fff; border-color: #000; }
  .pd-size-btn:disabled { opacity: 0.5; cursor: not-allowed; }

  .pd-size-compliment {
    margin-top: 10px;
    font-size: 9px;
    font-weight: 300;
    letter-spacing: 0.15em;
    color: black;
    font-style: italic;
  }

  /* Quantity */
  .pd-qty {
    display: inline-flex;
    align-items: center;
    border: 1px solid black;
  }

  .pd-qty-btn {
    width: 36px; height: 36px;
    background: transparent;
    border: none;
    font-family: 'Exo 2', sans-serif;
    font-size: 14px;
    font-weight: 300;
    color: black;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background 0.15s, color 0.15s;
  }
  .pd-qty-btn:hover:not(:disabled) { background: #000; color: #fff; }
  .pd-qty-btn:disabled { opacity: 0.25; cursor: not-allowed; }

  .pd-qty-num {
    width: 44px;
    text-align: center;
    font-size: 13px;
    font-weight: 400;
    letter-spacing: 0.1em;
    color: #000;
    border-left: 1px solid black;
    border-right: 1px solid black;
    line-height: 36px;
  }

  /* Shipping */
  .pd-shipping-note {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 9px;
    font-weight: 400;
    letter-spacing: 0.3em;
    text-transform: uppercase;
    color: black;
    margin-bottom: 16px;
  }

  /* Add to Cart */
  .pd-atc-btn {
    width: 100%;
    background: #000;
    color: #fff;
    border: none;
    font-family: 'Exo 2', sans-serif;
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.4em;
    text-transform: uppercase;
    padding: 18px;
    cursor: pointer;
    transition: background 0.2s ease;
    margin-bottom: 12px;
  }
  .pd-atc-btn:hover:not(:disabled) { background: #222; }
  .pd-atc-btn:disabled { background: rgba(0,0,0,0.2); cursor: not-allowed; }
  .pd-atc-btn--out { background: rgba(0,0,0,0.15); color: rgba(0,0,0,0.4); }

  .pd-restock {
    text-align: center;
    margin-bottom: 12px;
  }
  .pd-restock-text {
    font-size: 9px;
    font-weight: 300;
    letter-spacing: 0.15em;
    color: black;
    margin-bottom: 6px;
  }
  .pd-restock-link {
    font-size: 9px;
    font-weight: 500;
    letter-spacing: 0.25em;
    text-transform: uppercase;
    color: #000;
    background: transparent;
    border: none;
    cursor: pointer;
    text-decoration: underline;
  }

  .pd-added-note {
    font-size: 9px;
    font-weight: 300;
    letter-spacing: 0.2em;
    color: black;
    text-align: center;
    text-transform: uppercase;
  }

  /* ── ACCORDIONS ── */
  .pd-accordions { display: flex; flex-direction: column; gap: 0; }

  .pd-accordion { border-top: 1px solid black; }
  .pd-accordion:last-child { border-bottom: 1px solid black; }

  .pd-accordion-trigger {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 0;
    background: transparent;
    border: none;
    cursor: pointer;
    font-family: 'Exo 2', sans-serif;
    font-size: 9px;
    font-weight: 500;
    letter-spacing: 0.35em;
    text-transform: uppercase;
    color: #000;
    text-align: left;
  }

  .pd-accordion-icon {
    color: black;
    transition: transform 0.25s ease;
    display: flex;
  }

  .pd-accordion-body {
    overflow: hidden;
    transition: max-height 0.35s ease;
  }

  .pd-accordion-content {
    padding: 4px 0 18px;
  }

  .pd-acc-text {
    font-size: 12px;
    font-weight: 300;
    color: black;
    line-height: 2;
    letter-spacing: 0.04em;
    margin-bottom: 10px;
    text-align: justify;
  }

  .pd-acc-label {
    font-size: 8px;
    font-weight: 600;
    letter-spacing: 0.4em;
    text-transform: uppercase;
    color: #000;
    margin-bottom: 4px;
  }

  .pd-acc-tip {
    font-size: 11px;
    font-weight: 300;
    font-style: italic;
    color: black;
    letter-spacing: 0.04em;
    line-height: 1.8;
    border-left: 2px solid black;
    padding-left: 12px;
    margin-bottom: 12px;
  }

  .pd-acc-link {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 9px;
    font-weight: 500;
    letter-spacing: 0.3em;
    text-transform: uppercase;
    color: #000;
    text-decoration: underline;
    text-underline-offset: 3px;
  }

  .pd-desc-block { margin-bottom: 8px; font-size: 12px; font-weight: 300; color: black; line-height: 2; letter-spacing: 0.04em; }

  .pd-care-list { display: flex; flex-direction: column; gap: 8px; }
  .pd-care-item { display: flex; gap: 10px; font-size: 12px; font-weight: 300; color: black; letter-spacing: 0.04em; line-height: 1.6; }
  .pd-care-dash { color: black; flex-shrink: 0; }

  /* Brand / Material */
  .pd-meta {
    margin-top: 20px;
    display: flex;
    flex-direction: column;
    gap: 0;
    border-top: 1px solid black;
  }

  .pd-meta-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 0;
    border-bottom: 1px solid black;
  }
  .pd-meta-row:last-child { border-bottom: none; }

  .pd-meta-label {
    font-size: 8px;
    font-weight: 500;
    letter-spacing: 0.4em;
    text-transform: uppercase;
    color: black;
  }

  .pd-meta-value {
    font-size: 11px;
    font-weight: 400;
    letter-spacing: 0.1em;
    color: #000;
  }

  /* ── SIMILAR PRODUCTS ── */
  .pd-similar {
    margin-top: 80px;
    padding-top: 0;
  }

  .pd-similar-header { margin-bottom: 24px; }

  .pd-similar-eyebrow {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 8px;
    font-weight: 400;
    letter-spacing: 0.45em;
    text-transform: uppercase;
    color: black;
    margin-bottom: 10px;
  }
  .pd-similar-eyebrow::before {
    content: '';
    width: 20px; height: 1px;
    background: black;
    display: block; flex-shrink: 0;
  }

  .pd-similar-title {
    font-size: clamp(22px, 3vw, 40px);
    font-weight: 200;
    color: #000;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    line-height: 1;
    margin: 0;
  }
  .pd-similar-bold { font-weight: 600; }

  .pd-similar-rule hr {
    border: none;
    border-top: 1px solid rgba(0,0,0,0.08);
    margin: 0 0 0;
  }

  /* ── SCROLL TO TOP ── */
  .pd-scroll-top {
    position: fixed;
    bottom: 24px;
    right: 24px;
    width: 40px; height: 40px;
    background: #000;
    color: #fff;
    border: none;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 50;
    transition: background 0.2s;
  }
  .pd-scroll-top:hover { background: #222; }

  /* ── RESPONSIVE ── */
  @media (max-width: 1024px) {
    .pd-root { padding: 40px 40px 80px; }
    .pd-grid { gap: 40px; }
  }

  @media (max-width: 768px) {
    .pd-root { padding: 24px 16px 64px; }
    .pd-grid { grid-template-columns: 1fr; gap: 32px; }
    .pd-gallery { position: static; }
    .pd-name { font-size: 20px; }
    .pd-price { font-size: 20px; }
    .pd-atc-btn { padding: 16px; }
  }

  @media (max-width: 360px) {
    .pd-root { padding: 16px 12px 48px; }
  }

  /* ── MYSTERY PRODUCT ── */

  .pd-mystery-img-overlay {
    position: absolute;
    inset: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 10px;
    z-index: 4;
    pointer-events: none;
  }

  .pd-mystery-img-label {
    font-family: 'Exo 2', sans-serif;
    font-size: 8px;
    font-weight: 600;
    letter-spacing: 0.5em;
    text-transform: uppercase;
    color: rgba(255,255,255,0.8);
  }

  .pd-mystery-lock {
    display: flex;
    flex-direction: column;
    gap: 0;
    margin-bottom: 16px;
  }

  .pd-mystery-lock-header {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 8px;
    font-weight: 600;
    letter-spacing: 0.45em;
    text-transform: uppercase;
    color: #000;
    margin-bottom: 14px;
  }

  .pd-mystery-input-row {
    display: flex;
    gap: 0;
    border: 1px solid #000;
    margin-bottom: 8px;
  }

  .pd-mystery-input {
    flex: 1;
    background: transparent;
    border: none;
    outline: none;
    font-family: 'Exo 2', sans-serif;
    font-size: 18px;
    font-weight: 300;
    letter-spacing: 0.6em;
    text-align: center;
    color: #000;
    padding: 14px 16px;
    min-width: 0;
    transition: background 0.2s;
  }

  .pd-mystery-input::placeholder {
    color: rgba(0,0,0,0.2);
    letter-spacing: 0.45em;
  }

  .pd-mystery-input--error {
    background: rgba(220,0,0,0.04);
    animation: pd-shake 0.35s ease;
  }

  @keyframes pd-shake {
    0%,100% { transform: translateX(0); }
    20% { transform: translateX(-6px); }
    40% { transform: translateX(6px); }
    60% { transform: translateX(-4px); }
    80% { transform: translateX(4px); }
  }

  .pd-mystery-submit {
    background: #000;
    color: #fff;
    border: none;
    font-family: 'Exo 2', sans-serif;
    font-size: 8px;
    font-weight: 600;
    letter-spacing: 0.4em;
    text-transform: uppercase;
    padding: 0 20px;
    cursor: pointer;
    flex-shrink: 0;
    transition: background 0.2s;
  }

  .pd-mystery-submit:disabled {
    background: rgba(0,0,0,0.25);
    cursor: not-allowed;
  }

  .pd-mystery-submit:not(:disabled):hover { background: #222; }

  .pd-mystery-error {
    font-size: 9px;
    font-weight: 400;
    letter-spacing: 0.15em;
    color: #c00;
    text-align: center;
    margin-bottom: 8px;
  }

  .pd-mystery-hint {
    margin-top: 20px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
    text-align: center;
  }

  .pd-mystery-hint-rule {
    width: 24px;
    height: 1px;
    background: rgba(0,0,0,0.25);
  }

  .pd-mystery-hint-text {
    font-size: 10px;
    font-weight: 300;
    letter-spacing: 0.12em;
    color: rgba(0,0,0,0.55);
    line-height: 1.7;
    max-width: 320px;
    margin: 0;
  }

  .pd-mystery-newsletter-btn {
    font-family: 'Exo 2', sans-serif;
    font-size: 8px;
    font-weight: 600;
    letter-spacing: 0.4em;
    text-transform: uppercase;
    color: #000;
    background: transparent;
    border: none;
    border-bottom: 1px solid #000;
    padding-bottom: 2px;
    cursor: pointer;
  }

  .pd-mystery-newsletter-btn:hover { opacity: 0.6; }

`;

export default ProductDetails;
